import type { ClassDefinition } from '../../shared/game/class-types.js';
import type { EnemyDefinition } from '../../shared/game/world-types.js';
import { STANDARD_MELEE_REACH, WALK_SPEED, speedForMovementDistance } from '../../shared/game/combat-distances.js';

/**
 * A Shadow (Shadowmancer3.0.pdf, Summon Shadow): a d12 minion. Like every minion it dies to any damage and cannot crit; it fights only
 * when its Shadowmancer commands it and leaves as soon as the fight is over. `id`, `spawn` and `summoner` are filled in when it is called.
 */
export const shadowTemplate: EnemyDefinition = {
  id: 'shadow', name: 'Shadow', kind: 'minion', family: 'shadow', factionId: 'party', behavior: 'melee',
  color: 0x6d6280, speed: speedForMovementDistance(WALK_SPEED, 9), radius: .38, spawn: { x: 0, z: 0 },
  profile: {
    maxHp: 1, attributes: { strength: 0, dexterity: 0, intelligence: 0, will: 0 }, defense: 0, armor: 0, maxWounds: 0, movementDistance: 9,
    attacks: [{ id: 'shadow-touch', name: 'Shadow Touch', mode: 'melee', count: 1, sides: 12, stat: null, bonus: 0, damageType: 'necrotic', range: STANDARD_MELEE_REACH, canCrit: false }],
  },
  // The body's smoke, its thinner tendrils and the claws; the eyes always burn white.
  appearance: { rig: 'shadow', ancestry: 'shadow', archetype: 'cloth', height: 1.6, cloth: 0x0b0a0d, leather: 0x141217, metal: 0x2a2630, skin: 0x1b191d, hair: 0x0b0a0d, eyes: 0xffffff },
};

// Shadowmancer 3.0 draft, level 1 (Conduit of Shadow). Master of Darkness, Pilfered Power, invocations and subclasses come later.
export const shadowmancer: ClassDefinition = {
  id: 'shadowmancer', name: 'Shadowmancer',
  summary: 'A shadowy master of expendable hordes, drawing on a pact with an ancient, unfathomable patron.',
  keyStats: ['intelligence', 'dexterity'], hitDie: 8, startingHp: 13,
  saves: { advantage: ['intelligence'], disadvantage: ['will'] },
  armor: ['cloth'], weapons: ['dexterity'], weaponTypes: ['wand'],
  startingGear: ['Adventurer’s Garb', 'Sickle', 'Shovel'], defaultStance: 'keep-distance',
  abilities: [
    {
      id: 'shadow-blast', spell: true, name: 'Shadow Blast', level: 1, actions: 1, target: 'enemy', oncePerRound: true, autocast: true, autoAttack: true,
      description: 'Shadowmancer cantrip · Range 8 · 1d12 + DEX necrotic. Once per round.',
    },
    {
      id: 'summon-shadow', spell: true, name: 'Summon Shadow', level: 1, actions: 1, target: 'self', combatOnly: true, autocast: true,
      summon: { template: shadowTemplate, limit: 'intelligence' },
      description: 'Shadowmancer cantrip · Summon a Shadow beside you: a d12 minion that dies to any damage. Up to INT Shadows; they leave when the fight ends.',
    },
    {
      id: 'command-shadows', spell: true, name: 'Command Shadows', level: 1, actions: 1, target: 'enemy', oncePerRound: true, combatOnly: true, autocast: true,
      description: 'Shadowmancer cantrip · Reach 6 · All your Shadows go for this foe, or your nearest one, and attack it. Once per round.',
    },
  ],
  passives: [{ id: 'conduit-of-shadow', name: 'Conduit of Shadow', level: 1, description: 'You know the Shadowmancer cantrips.' }],
};
